<template>
  <div id="servicos-categorias-modal-app">
    <modal
      elem-id="modal_crud_servicos_categorias"
      size="small"
      :close-label="t('Fechar')"
      :save-label="t('Salvar')"
      :save-disabled-fn="() => !formRef?.canSave"
      :save-fn="handleSave"
      :title="modalTitle"
      :show="isVisible"
      @close="closeModal"
      @dismiss="closeModal"
    >
      <!-- Content only renders when modal is visible (prevents API calls on mount) -->
      <template v-if="isVisible">
        <!-- Action Buttons -->
        <div class="text-right" style="margin-bottom: 20px;">
        <Button
          v-if="shouldShowDelete"
          :label="t('Excluir')"
          icon="pi pi-trash"
          severity="danger"
          data-testid="servicos-categorias-delete-button"
          @click="handleDelete" />
      </div>

        <!-- Form -->
        <servicos-categorias-form
          ref="formRef"
          :categoria-id="currentCategoriaId"
          @saved="handleSaveSuccess"
          @cancel="closeModal">
        </servicos-categorias-form>
      </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useServicosCategorias } from '../../composables/useServicosCategorias';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { log } from '../../utils/DebugLogger';
import ServicosCategoriasForm from './ServicosCategoriasForm.vue';

interface FormRefExposed {
  initializeForNew: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  canSave: boolean;
}

interface ModalOpenPayload {
  id?: number | string | null;
}

const { t } = useI18n();
const { deleteCategoria } = useServicosCategorias();
const { confirmAction } = useConfirmAction();

const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const formRef = ref<FormRefExposed | null>(null);
const currentCategoriaId = ref<number | string | null>(null);

const modalTitle = computed<string>(() => {
  return isEditMode.value ? t('Editar Categoria') : t('Nova Categoria');
});

const shouldShowDelete = computed<boolean>(() => {
  return isEditMode.value && !!currentCategoriaId.value;
});

const openForNew = async (): Promise<void> => {
  log('ServicosCategoriasModal', 'openForNew', 'Opening modal for new categoria');
  isVisible.value = true;
  isEditMode.value = false;
  currentCategoriaId.value = null;

  await nextTick();

  if (formRef.value) {
    await formRef.value.initializeForNew();
  }
};

const openForEdit = async (categoriaId: number | string): Promise<void> => {
  log('ServicosCategoriasModal', 'openForEdit', 'Opening modal for edit', categoriaId);
  isVisible.value = true;
  isEditMode.value = true;
  currentCategoriaId.value = categoriaId;
};

const closeModal = (): void => {
  log('ServicosCategoriasModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  currentCategoriaId.value = null;
};

const handleSave = async (): Promise<void> => {
  log('ServicosCategoriasModal', 'handleSave', 'Saving categoria');
  if (formRef.value) {
    await formRef.value.handleSubmit();
  }
};

const handleSaveSuccess = (): void => {
  log('ServicosCategoriasModal', 'handleSaveSuccess', 'Categoria saved successfully');
  closeModal();
  eventBus.emit('servicos-categorias.refresh');
};

const handleDelete = (): void => {
  if (!currentCategoriaId.value) return;

  confirmAction({
    message: t('Tem certeza que deseja excluir esta categoria?'),
    header: t('Confirmar Exclusão'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('Sim'),
    rejectLabel: t('Não'),
    onAccept: async () => {
      log('ServicosCategoriasModal', 'handleDelete', 'Deleting categoria', currentCategoriaId.value);

      try {
        await deleteCategoria(currentCategoriaId.value);
        log('ServicosCategoriasModal', 'handleDelete', 'Categoria deleted successfully');

        eventBus.emit('globalMessage.success', t('Categoria excluída com sucesso'));
        closeModal();
        eventBus.emit('servicos-categorias.refresh');
      } catch (error) {
        log('ServicosCategoriasModal', 'handleDelete', 'Error deleting categoria', error);
        eventBus.emit('globalMessage.error', t('Erro ao excluir categoria'));
      }
    }
  });
};

const handleModalOpen = async (data: ModalOpenPayload | undefined): Promise<void> => {
  log('ServicosCategoriasModal', 'handleModalOpen', 'Received open event', data);
  if (data?.id) {
    await openForEdit(data.id);
  } else {
    await openForNew();
  }
};

const handleModalClose = (): void => {
  log('ServicosCategoriasModal', 'handleModalClose', 'Received close event');
  closeModal();
};

onMounted(() => {
  log('ServicosCategoriasModal', 'mounted', 'Component mounted, registering eventBus listeners');
  eventBus.on('modal.servicos-categorias.open', handleModalOpen);
  eventBus.on('modal.servicos-categorias.close', handleModalClose);
});

onUnmounted(() => {
  log('ServicosCategoriasModal', 'unmounted', 'Component unmounted, removing eventBus listeners');
  eventBus.off('modal.servicos-categorias.open', handleModalOpen);
  eventBus.off('modal.servicos-categorias.close', handleModalClose);
});
</script>

<style scoped>
/* Modal styles are handled by Modal component */
</style>
